/// <amd-module name="RMWidgets/DeploymentStatusWidget/DSWUtils" />
import { format, localeFormat, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { Statuses, IStatusProps } from "VSSUI/Status";

import { DefinitionType, Build } from "RMWidgets/DeploymentStatusWidget/Clients/Contracts/Build";

import {
    ReleaseReference,
    TestResultSummary,
    TestOutcome,
    TestRunState
} from "RMWidgets/DeploymentStatusWidget/Clients/Contracts/Test";

import {
    DefinitionEnvironmentReference,
    ReleaseDefinition,
    ReleaseDefinitionEnvironment,
    DeploymentStatus,
    DeploymentOperationStatus,
    DeploymentExpands,
    DeploymentQueryParameters,
    DeploymentsQueryType,
    Deployment,
    Artifact
} from "Release/Client/Contracts/Release";
import {
    createReleaseEnvironmentUrl,
    createReleaseLogsUrl,
    createTestResultsTabUrl
} from "Release/Helpers/Link";
import { parseDuration, diffDate } from "Release/Helpers/DateTime"

import {
    IDSWEnvironmentInfo,
    IDSWDeploymentInfo,
    IDSWTestResultInfo,
    DSWTooltip,
    IReleaseEnvironmentReference,
    TestRateRuleColors,
    TestRateRuleOperators
} from "RMWidgets/DeploymentStatusWidget/DSWContracts";
import {
    ArtifactDefinitionConstants,
    BranchNamePrefix,
    WellKnownRepositoryTypes
} from "RMWidgets/DeploymentStatusWidget/DSWConstants";
import * as Resources from "RMWidgets/DeploymentStatusWidget/Resources";

export function getBuildTooltip(build: Build): DSWTooltip {
    let toolTip: DSWTooltip = {};

    toolTip.build = build.buildNumber;
    toolTip.buildStatus = Resources.DSWBuildStatusCompleted;

    return toolTip;
}

export function getBuildInfoListFromDeployments(deployments: Deployment[], buildDefinitionId: number, branchName: string): any[] {
    if (deployments.length <= 0) {
        return [];
    }

    let builds: any[] = [];
    let map: { [buildId: string]: boolean } = {};

    deployments.forEach((deployment: Deployment) => {
        let artifacts = deployment.release.artifacts;
        let artifactType = getDefinitionTypeStringMapping(DefinitionType.Build);

        if (artifacts.length > 0) {
            artifacts.forEach((artifact: Artifact) => {
                if (artifact.type === artifactType
                    && artifact.definitionReference
                    && artifact.definitionReference[ArtifactDefinitionConstants.Definition]
                    && artifact.definitionReference[ArtifactDefinitionConstants.Definition].id === buildDefinitionId.toString()
                    && artifact.definitionReference[ArtifactDefinitionConstants.Branch]
                    && localeIgnoreCaseComparer(artifact.definitionReference[ArtifactDefinitionConstants.Branch].name, branchName) === 0
                    && artifact.definitionReference[ArtifactDefinitionConstants.Version]
                    && !map[artifact.definitionReference[ArtifactDefinitionConstants.Version].id]) {

                    let version = artifact.definitionReference[ArtifactDefinitionConstants.Version];
                    let buildInfo = {
                        id: parseInt(version.id),
                        buildNumber: version.name,
                        url: artifact.definitionReference.hasOwnProperty(ArtifactDefinitionConstants.ArtifactSourceVersionUrl) ?
                            artifact.definitionReference[ArtifactDefinitionConstants.ArtifactSourceVersionUrl].id : ""
                    };

                    builds.push(buildInfo);
                    map[version.id] = true;
                }
            });
        }
    });

    return builds.sort((x, y) => { return x.id - y.id; });
}

export function getFullBranchName(repositoryType: string, branchName: string) {
    if (_mustEnsureRefPrefix(repositoryType)) {
        return getRefPrefixedBranchName(branchName);
    }

    return branchName;
}

function getRefPrefixedBranchName(branchName: string): string {
    if (branchName) {
        if (branchName.indexOf(BranchNamePrefix) === 0) {
            return branchName;
        }
        else {
            return format("{0}{1}", BranchNamePrefix, branchName);
        }
    }

    return "";
}

function _getDeploymentStatusMap(status: DeploymentStatus): IStatusProps {
    switch (status) {
        case DeploymentStatus.InProgress:
            return Statuses.Running;
        case DeploymentStatus.Failed:
            return Statuses.Failed;
        case DeploymentStatus.Succeeded:
            return Statuses.Success;
        case DeploymentStatus.PartiallySucceeded:
            return Statuses.Warning;
        case DeploymentStatus.NotDeployed:
        case DeploymentStatus.Undefined:
        default:
            return Statuses.Skipped;
    }
}

export function getTestResultsStatusMap(color: TestRateRuleColors): IStatusProps {
    switch (color) {
        case TestRateRuleColors.Green:
            return Statuses.Success;
        case TestRateRuleColors.Red:
            return Statuses.Failed;
        case TestRateRuleColors.Orange:
            return Statuses.Warning;
        default:
            return Statuses.Skipped;
    }
}

export function getBuildIdsFromDeploymentInfo(deployment: Deployment, buildDefinitionId: number): number[] {
    let artifacts = deployment.release.artifacts;
    let buildIds: number[] = [];
    let artifactType = getDefinitionTypeStringMapping(DefinitionType.Build);

    if (artifacts && artifacts.length > 0) {

        buildIds = artifacts.map((artifact: Artifact) => {
            if (artifact.type === artifactType
                && artifact.definitionReference
                && artifact.definitionReference[ArtifactDefinitionConstants.Definition]
                && artifact.definitionReference[ArtifactDefinitionConstants.Definition].id === buildDefinitionId.toString()) {
                return parseInt(artifact.definitionReference[ArtifactDefinitionConstants.Version].id);
            }

            return 0;
        });
    }

    return !buildIds ? []
        : buildIds.filter(function (value, index, self) {
            return self.indexOf(value) === index;
        });
}

export function getTestResultInfo(pageContext: IVssPageContext, testResult: TestResultSummary): IDSWTestResultInfo {
    let releaseReference = testResult.testResultsContext.release;
    let passPercentage = localeFormat("{0}%", _getPassPercent(testResult).toString());
    let testResultsUrl = createTestResultsTabUrl(pageContext, releaseReference.id, releaseReference.environmentId);
    let tooltip = _getTestResultsTooltip(pageContext, testResult);

    return <IDSWTestResultInfo>{
        releaseId: testResult.testResultsContext.release.id,
        releaseEnvironmentId: testResult.testResultsContext.release.environmentId,
        passPercentage: passPercentage,
        testResultsUrl: testResultsUrl,
        toolTip: tooltip
    };
}

function _getTestResultsTooltip(pageContext: IVssPageContext, testResult: TestResultSummary): DSWTooltip {
    let toolTip: DSWTooltip = {};
    let passedTestsCount = (testResult.aggregatedResultsAnalysis.resultsByOutcome[TestOutcome.Passed]) ? testResult.aggregatedResultsAnalysis.resultsByOutcome[TestOutcome.Passed].count : 0x00;
    let totalTestsCount = testResult.aggregatedResultsAnalysis.totalTests || 0x01;

    toolTip.testsPassed = localeFormat(Resources.DeploymentStatusWidgetTestsToolTip, passedTestsCount, totalTestsCount);
    toolTip.testRunDuration = parseDuration(testResult.aggregatedResultsAnalysis.duration);

    if (testResult.aggregatedResultsAnalysis.runSummaryByState) {
        let abortedRunsSummary = testResult.aggregatedResultsAnalysis.runSummaryByState[TestRunState.Aborted];

        if (abortedRunsSummary) {
            toolTip.abortedRuns = localeFormat("{0}",
                abortedRunsSummary.runsCount);
        }
    }

    return toolTip;
}

function _getPassPercent(testResult: TestResultSummary): number {
    let denominator = testResult.aggregatedResultsAnalysis.totalTests || 0x01;
    let numerator = (testResult.aggregatedResultsAnalysis.resultsByOutcome[TestOutcome.Passed])
        ? testResult.aggregatedResultsAnalysis.resultsByOutcome[TestOutcome.Passed].count
        : 0x00;

    let passPercent = (numerator / denominator) * 100;

    return (passPercent === Math.round(passPercent))
        ? passPercent
        : _getCustomizedDecimalValue(passPercent);
}

// Truncates the decimal number to restrict no of digits after the decimal point to 2
// Eg: 99.67392809 => 99.67. 0.0001 => 0
function _getCustomizedDecimalValue(value: number): number {
    return ((Math.floor(value * 100)) / 100);
}

export function constructDeploymentInfo(pageContext: IVssPageContext, deployment: Deployment): IDSWDeploymentInfo {
    let toolTip = _getDeploymentTooltip(deployment);

    let deploymentInfo: IDSWDeploymentInfo = {
        deploymentId: deployment.id,
        releaseId: deployment.release.id,
        releaseName: deployment.release.name,
        releaseEnvironmentId: deployment.releaseEnvironment.id,
        releaseEnvironmentName: deployment.releaseEnvironment.name,
        releaseSummaryUrl: createReleaseLogsUrl(pageContext, deployment.release.id, deployment.releaseEnvironment.id),
        statusColourCss: _getDeploymentStatusCssMap(deployment.deploymentStatus),
        statusProps: _getDeploymentStatusMap(deployment.deploymentStatus),
        toolTip: toolTip
    };

    return deploymentInfo;
}

export function getReleaseDefinitionIds(environments: DefinitionEnvironmentReference[]): number[] {
    let allDefinitionIds = environments.map((env) => {
        return env.releaseDefinitionId;
    });

    let uniqueIds = allDefinitionIds.filter(function (value, index, self) {
        return self.indexOf(value) === index;
    });

    return uniqueIds;
}

export function constructEnvInfo(
    pageContext: IVssPageContext,
    releaseDefinition: ReleaseDefinition,
    environment: ReleaseDefinitionEnvironment): IDSWEnvironmentInfo {
    return {
        definitionEnvironmentId: environment.id,
        definitionEnvironmentName: environment.name,
        releaseDefinitionId: releaseDefinition.id,
        releaseDefinitionName: releaseDefinition.name,
        environmentSummaryUrl: createReleaseEnvironmentUrl(pageContext, environment.id, releaseDefinition.id),
        tooltip: getEnvironmentInfoTooltip(releaseDefinition.name, environment.name)
    };
}

function getEnvironmentInfoTooltip(releaseDefinitionName: string, environmentName: string): string[] {
    let tooltip: string[] = [];

    tooltip.push(releaseDefinitionName);
    tooltip.push(environmentName);

    return tooltip;
}

export function getTestReleaseReferences(projectId: string, releaseEnvironmentReferences: IReleaseEnvironmentReference[]): ReleaseReference[] {
    if (!releaseEnvironmentReferences || releaseEnvironmentReferences.length <= 0) {
        return [];
    }

    let references = releaseEnvironmentReferences.map((reference: IReleaseEnvironmentReference) => {
        return <ReleaseReference>{
            id: reference.releaseId,
            environmentId: reference.environmentId
        };
    });

    return references;
}

export function getReleaseEnvironmentReferences(deployments: Deployment[]): IReleaseEnvironmentReference[] {
    let references: IReleaseEnvironmentReference[] = [];

    if (!deployments || deployments.length <= 0) {
        return references;
    }

    deployments.forEach((deployment: Deployment) => {
        if (deployment.deploymentStatus === DeploymentStatus.Failed
            || deployment.deploymentStatus === DeploymentStatus.PartiallySucceeded
            || deployment.deploymentStatus === DeploymentStatus.Succeeded) {
            references.push(<IReleaseEnvironmentReference>{ releaseId: deployment.release.id, environmentId: deployment.releaseEnvironment.id });
        }
    });

    return references;
}

export function getTestPercentageStatusCssMap(percentage: string, ruleBoundaries: string[], ruleOperators: string[]): string {
    let testPassedRate: number = parseInt(percentage);
    let css: string = "";
    let testPercentCssMap = [TestRateRuleColors.Green, TestRateRuleColors.Orange, TestRateRuleColors.Red];
    if (!isNaN(testPassedRate)) {
        for (let index in ruleBoundaries) {
            let boundary: number = parseInt(ruleBoundaries[index]);
            if (!isNaN(boundary)) {
                if (compareWithGivenOperator(testPassedRate, ruleOperators[index], boundary)) {
                    css = testPercentCssMap[index];
                }
            }
        }

        return css;
    } else {

        return "";
    }
}

export function compareWithGivenOperator(operand1: number, operator: string, operand2: number): boolean {
    if (isNaN(operand1) || isNaN(operand2)) {
        return false;
    }

    switch (operator) {
        case TestRateRuleOperators.LessThan:
            return (operand1 < operand2);
        case TestRateRuleOperators.GreaterThan:
            return (operand1 > operand2);
        case TestRateRuleOperators.LessThanEquals:
            return (operand1 <= operand2);
        case TestRateRuleOperators.GreaterThanEquals:
            return (operand1 >= operand2);
        case TestRateRuleOperators.Equals:
            return (operand1 === operand2);
        default:
            return false;
    }
}

export function getDeploymentQueryParameters(
    projectId: string,
    environmentReference: DefinitionEnvironmentReference[],
    buildDefinitionId: number,
    artifactType: string,
    sourceBranch: string,
    versions: string[],
    noOfBuilds: number): DeploymentQueryParameters {

    let deploymentStatus: DeploymentStatus = DeploymentStatus.Failed
        | DeploymentStatus.InProgress
        | DeploymentStatus.PartiallySucceeded
        | DeploymentStatus.Succeeded;

    let operationStatus: DeploymentOperationStatus = DeploymentOperationStatus.Approved
        | DeploymentOperationStatus.Canceled
        | DeploymentOperationStatus.Cancelling
        | DeploymentOperationStatus.Pending
        | DeploymentOperationStatus.PhaseCanceled
        | DeploymentOperationStatus.PhaseFailed
        | DeploymentOperationStatus.PhaseInProgress
        | DeploymentOperationStatus.PhasePartiallySucceeded
        | DeploymentOperationStatus.PhaseSucceeded
        | DeploymentOperationStatus.Rejected
        | DeploymentOperationStatus.ManualInterventionPending
        | DeploymentOperationStatus.Queued
        | DeploymentOperationStatus.QueuedForAgent
        | DeploymentOperationStatus.QueuedForPipeline
        | DeploymentOperationStatus.EvaluatingGates
        | DeploymentOperationStatus.GateFailed;

    let deploymentExpands: DeploymentExpands = DeploymentExpands.DeploymentOnly
        | DeploymentExpands.Artifacts;

    let queryParameters = <DeploymentQueryParameters>{
        artifactSourceId: getArtifactSourceIdFromBDId(projectId, buildDefinitionId),
        artifactTypeId: artifactType,
        artifactVersions: versions,
        deploymentStatus: deploymentStatus,
        operationStatus: operationStatus,
        environments: environmentReference,
        expands: deploymentExpands,
        deploymentsPerEnvironment: noOfBuilds,
        sourceBranch: sourceBranch,
        queryType: DeploymentsQueryType.Regular
    };

    return queryParameters;
}

export function getArtifactSourceIdFromBDId(projectId: string, buildDefinitionid: number): string {
    return format("{0}:{1}", projectId, buildDefinitionid.toString());
}

function _mustEnsureRefPrefix(repoType: string) {
    if (!repoType) {
        return false;
    }

    if (repoType === WellKnownRepositoryTypes.Git
        || repoType === WellKnownRepositoryTypes.GitHub
        || repoType === WellKnownRepositoryTypes.GitHubEnterprise
        || repoType === WellKnownRepositoryTypes.TfsGit
        || repoType === WellKnownRepositoryTypes.Bitbucket) {
        return true;
    }

    return false;
}

function _getDeploymentTooltip(deployment: Deployment): DSWTooltip {
    let toolTip: DSWTooltip = {};

    toolTip.release = deployment.release.name;
    toolTip.environment = deployment.releaseEnvironment.name;
    toolTip.deploymentStatus = getDeploymentStatusMapping(deployment.deploymentStatus);

    if (_isDeploymentInTerminalState(deployment.deploymentStatus)) {
        toolTip.deploymentDuration = diffDate(deployment.lastModifiedOn, deployment.startedOn);
    }

    return toolTip;
}

function _isDeploymentInTerminalState(deploymentStatus: DeploymentStatus) {
    if (deploymentStatus === DeploymentStatus.Failed
        || deploymentStatus === DeploymentStatus.Succeeded
        || deploymentStatus === DeploymentStatus.PartiallySucceeded) {

        return true;
    }

    return false;
}

function _getDeploymentStatusCssMap(status: DeploymentStatus): string {
    switch (status) {
        case DeploymentStatus.NotDeployed:
        case DeploymentStatus.Undefined:
            return "cell-status-na";
        case DeploymentStatus.InProgress:
            return "cell-status-inprogress";
        case DeploymentStatus.Failed:
            return "cell-status-failure";
        case DeploymentStatus.Succeeded:
            return "cell-status-success";
        case DeploymentStatus.PartiallySucceeded:
            return "cell-status-warning";
        default:
            return "";
    }
}

export function getDefinitionTypeStringMapping(type: DefinitionType): string {
    switch (type) {
        case DefinitionType.Build: return "Build";
        case DefinitionType.Xaml: return "Xaml";
        default: return "";
    }
}

export function getDeploymentStatusMapping(deploymentStatus: DeploymentStatus): string {
    switch (deploymentStatus) {
        case DeploymentStatus.All: return Resources.DeploymentStatus_All;
        case DeploymentStatus.Failed: return Resources.DeploymentStatus_Failed;
        case DeploymentStatus.InProgress: return Resources.DeploymentStatus_InProgress;
        case DeploymentStatus.NotDeployed: return Resources.DeploymentStatus_NotDeployed;
        case DeploymentStatus.PartiallySucceeded: return Resources.DeploymentStatus_PartiallySucceeded;
        case DeploymentStatus.Succeeded: return Resources.DeploymentStatus_Succeeded;
        case DeploymentStatus.Undefined: return Resources.DeploymentStatus_Undefined;
    }
}
